import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { apiClient } from '../auth/api';

export default function ProductUpdate() {
  const [query, setQuery] = useState(''); // legacy: product id or sku (not used now)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [listQuery, setListQuery] = useState('');
  const [listOffset, setListOffset] = useState(0);
  const listLimit = 500;
  const [selectedId, setSelectedId] = useState(null);
  const [expandedVariants, setExpandedVariants] = useState({});
  const [coverUploadError, setCoverUploadError] = useState('');
  const [variantUploadErrors, setVariantUploadErrors] = useState({});
  const [collapsedVariants, setCollapsedVariants] = useState({}); // Track collapsed state for similar variants

  const generateVariantSku = (baseSku, size, color) => {
    const sizeCode = size ? `-${String(size).toUpperCase()}` : '';
    const colorCode = color ? `-${String(color).toUpperCase().replace(/\s+/g, '')}` : '';
    return `${baseSku || ''}${sizeCode}${colorCode}`;
  };

  // Create multiple variants from a comma-separated size field value
  const splitVariantBySizes = (variant, baseSku, existingVariants) => {
    if (!variant.size || !variant.size.includes(',')) return null;
    const sizes = variant.size.split(',').map(s => s.trim()).filter(Boolean);
    // Get all existing SKUs from variants and any saved variants in the database
    const usedSkus = new Set();
    if (existingVariants) {
      existingVariants.forEach(v => {
        if (v.sku && v.sku.trim() !== '') {
          usedSkus.add(v.sku);
        }
      });
    }
    // Also add SKUs from all current product variants to avoid conflicts
    if (product?.variants) {
      product.variants.forEach(v => {
        if (v.sku && v.sku.trim() !== '') {
          usedSkus.add(v.sku);
        }
      });
    }
    
    return sizes.map(size => {
      const sku = generateVariantSku(baseSku, size, variant.color);
      let uniqueSku = sku;
      let i = 1;
      while (usedSkus.has(uniqueSku)) {
        uniqueSku = sku + '-' + i;
        i++;
      }
      usedSkus.add(uniqueSku);
      return {
        ...variant,
        id: undefined,
        size,
        sku: uniqueSku,
      };
    });
  };

  // Process variants for saving - much simpler now with draft system
  const processVariantsForSaving = (variants, baseSku) => {
    const processedVariants = [];
    const usedSkus = new Set();
    
    for (const variant of variants) {
      // Only process variants that have sizes - completely skip variants without sizes
      if (!variant.size || variant.size.trim() === '') {
        console.log(`Skipping variant without size: ${variant.color || 'unnamed'} (draft: ${variant.isDraft})`);
        continue;
      }
      
      // Don't allow comma-separated sizes at this point - they should have been split already
      if (variant.size.includes(',')) {
        console.warn(`Found variant with comma-separated sizes during save: ${variant.size}. This should have been split already.`);
        continue;
      }
      
      // Generate SKU if needed
      let finalVariant = { ...variant };
      if (!finalVariant.sku || finalVariant.sku.trim() === '') {
        let newSku = generateVariantSku(baseSku, finalVariant.size, finalVariant.color);
        let counter = 1;
        while (usedSkus.has(newSku)) {
          newSku = generateVariantSku(baseSku, finalVariant.size, finalVariant.color) + `-${counter}`;
          counter++;
        }
        finalVariant.sku = newSku;
      }
      usedSkus.add(finalVariant.sku);
      processedVariants.push(finalVariant);
    }
    
    return { processedVariants };
  };

  // Group variants by color/material for collapsing
  const groupVariantsByAttributes = (variants) => {
    const groups = {};
    variants.forEach((variant, index) => {
      const key = `${variant.color || ''}-${variant.material || ''}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push({ variant, index });
    });
    return groups;
  };

  // Check if variants in a group should be collapsed by default
  const shouldCollapseGroup = (group) => {
    return group.length > 1 && group.every(item => 
      item.variant.color === group[0].variant.color && 
      item.variant.material === group[0].variant.material
    );
  };

  // Category filtering state
  const [categories, setCategories] = useState([]);
  const [parentCategoryId, setParentCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');

  // Editing state for product's own category/subcategory
  const [editParentCategoryId, setEditParentCategoryId] = useState('');
  const [editSubcategoryId, setEditSubcategoryId] = useState('');

  // Fetch categories (parents and subs) - use flat dropdown endpoint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/form-data/categories', { params: { limit: 200 } });
        if (!cancelled) setCategories(data.items || []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const parentCategories = useMemo(
    () => (categories || []).filter(c => !c.parent_id),
    [categories]
  );
  const subcategories = useMemo(
    () => (categories || []).filter(c => parentCategoryId && c.parent_id === Number(parentCategoryId)),
    [categories, parentCategoryId]
  );
  // Unfiltered lists for edit controls (avoid sidebar filter coupling)
  const allParentCategories = useMemo(
    () => (categories || []).filter(c => !c.parent_id),
    [categories]
  );
  const allSubcategories = useMemo(
    () => (categories || []).filter(c => c.parent_id),
    [categories]
  );

  const loadById = async (id, fallbackDescription) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get(`/products/${encodeURIComponent(id)}`);
      setProduct({
        id: data.id,
        vendor_id: data.vendor_id,
        sku: data.sku || '',
        name: data.name || '',
        display_name: data.display_name || '',
        description: (data.description ?? fallbackDescription ?? ''),
        product_url: data.product_url || '',
        price_cents: data.price_cents || 0,
        currency: data.currency || 'EUR',
        image_url: data.image_url || '',
        variants: Array.isArray(data.variants) ? data.variants.map(v => ({
          id: v.id,
          sku: v.sku || '',
          size: v.size || '',
          color: v.color || '',
          material: v.material || '',
          price_cents: v.price_cents || 0,
          currency: v.currency || data.currency || 'EUR',
          stock: v.stock || 0,
          images: Array.isArray(v.images) ? v.images : []
        })) : [],
      });
      // Initialize editable category selections from product categories
      try {
        const details = Array.isArray(data.category_details) ? data.category_details : [];
        // Prefer specific subcategory when available
        const firstSub = details.find(c => c && c.parent_id !== null && c.parent_id !== undefined);
        if (firstSub) {
          setEditParentCategoryId(String(firstSub.parent_id));
          setEditSubcategoryId(String(firstSub.id));
        } else {
          const firstParent = details.find(c => c && (c.parent_id === null || c.parent_id === undefined));
          if (firstParent) {
            setEditParentCategoryId(String(firstParent.id));
            setEditSubcategoryId('');
          } else {
            setEditParentCategoryId('');
            setEditSubcategoryId('');
          }
        }
      } catch {}
      setSelectedId(Number(id));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load product');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const saveCategories = async () => {
    if (!product?.id) return;
    setSaving(true);
    setError('');
    try {
      const ids = [];
      if (editSubcategoryId) {
        // Include both subcategory and its parent for clarity in listings
        ids.push(Number(editSubcategoryId));
        const sub = (allSubcategories || []).find(sc => String(sc.id) === String(editSubcategoryId));
        if (sub?.parent_id) ids.push(Number(sub.parent_id));
      } else if (editParentCategoryId) {
        ids.push(Number(editParentCategoryId));
      }
      await apiClient.put(`/products/${product.id}/categories`, { category_ids: ids });
      alert('Categories updated');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update categories');
    } finally {
      setSaving(false);
    }
  };

  const loadBySku = async (sku) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get(`/products/by-sku/${encodeURIComponent(sku)}?include_suggested=false`);
      if (!data?.product?.id) throw new Error('Not found');
      await loadById(data.product.id);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load by SKU');
      setProduct(null);
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    if (/^\d+$/.test(q)) return loadById(q);
    return loadBySku(q);
  };

  const loadList = async (opts = {}) => {
    setListLoading(true);
    setListError('');
    try {
      const offset = opts.offset ?? listOffset;
      const search = (opts.search ?? listQuery).trim();
      const params = new URLSearchParams();
      params.set('limit', String(listLimit));
      params.set('offset', String(offset));
      if (search) params.set('search', search);
      // Prefer subcategory if selected, otherwise use parent category for filtering
      const chosenCategoryIdRaw = (opts.categoryId !== undefined ? opts.categoryId : (subcategoryId || parentCategoryId));
      const chosenCategoryId = chosenCategoryIdRaw ? Number(chosenCategoryIdRaw) : null;
      if (chosenCategoryId) params.set('category_id', String(chosenCategoryId));
      const { data } = await apiClient.get(`/products?${params.toString()}`);
      let items = Array.isArray(data?.items) ? data.items : [];
      // Augment with products that belong to direct children of the selected PARENT category only
      if (chosenCategoryId && !subcategoryId) {
        try {
          const { data: catData } = await apiClient.get(`/categories/${chosenCategoryId}/products-simple`);
          const simpleItems = Array.isArray(catData?.items) ? catData.items : [];
          if (simpleItems.length > 0) {
            const mapped = simpleItems.map(p => ({
              id: p.id,
              sku: p.sku,
              name: p.name,
              display_name: p.display_name,
              image_url: p.image_url,
              price_cents: undefined,
              currency: undefined,
            }));
            // Merge unique by id
            const seen = new Set(items.map(x => x.id));
            for (const m of mapped) {
              if (!seen.has(m.id)) {
                items.push(m);
                seen.add(m.id);
              }
            }
          }
        } catch {}
      }
      setList(items.map(p => ({
        id: p.id,
        sku: p.sku,
        name: p.name,
        display_name: p.display_name,
        description: p.description,
        image_url: p.image_url,
        price_cents: p.price_cents,
        currency: p.currency,
      })));
    } catch (e) {
      setListError(e.response?.data?.message || 'Failed to load products');
      setList([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    // Initial load
    loadList({ offset: 0, search: '' });
  }, []);

  // Reload list when category filters change
  useEffect(() => {
    // Reset pagination when filters change
    setListOffset(0);
    loadList({ offset: 0 });
  }, [parentCategoryId, subcategoryId]);

  const saveCore = async () => {
    if (!product?.id) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        sku: product.sku,
        name: product.name,
        display_name: product.display_name,
        product_url: product.product_url,
        description: product.description,
        image_url: product.image_url,
        price_cents: product.price_cents,
        currency: product.currency,
        vendor_id: product.vendor_id,
      };
      await apiClient.put(`/products/${product.id}`, body);
      alert('Product updated');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveVariant = async (idx) => {
    const v = product?.variants?.[idx];
    if (!v) return;
    
    // Prevent saving draft variants individually
    if (v.isDraft) {
      alert('Draft variants cannot be saved individually. Please complete the variant (add color, size, etc.) and use "Save All Variants" to save everything at once.');
      return;
    }
    
    // Check if this variant has comma-separated sizes and needs to be split
    if (v.size && v.size.includes(',')) {
      alert('Please use the "Save All Variants" button to save variants with multiple sizes (comma-separated).');
      return;
    }
    
    // Check if variant has no size - refuse to save base color variants
    if (!v.size || v.size.trim() === '') {
      alert('Variants must have a size. Please add sizes like S, M, L or use comma-separated values like "S, M, L" to create multiple variants.');
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      if (!v.id) {
        const bodyCreate = {
          product_id: product.id,
          sku: v.sku || generateVariantSku(product.sku, v.size, v.color),
          size: v.size || undefined,
          color: v.color || undefined,
          material: v.material || undefined,
          price_cents: v.price_cents ?? product.price_cents ?? 0,
          currency: v.currency || product.currency || 'EUR',
        };
        const { data } = await apiClient.post('/product-variants', bodyCreate);
        setProduct(p => {
          const vs = [...p.variants];
          vs[idx] = { ...vs[idx], id: data.id, sku: data.sku || bodyCreate.sku };
          return { ...p, variants: vs };
        });
        // Upsert inventory stock for the newly created variant
        try {
          await apiClient.post('/inventory', { variant_id: data.id, stock: Number.isFinite(v.stock) ? Number(v.stock) : 0 });
        } catch (_) { /* ignore inventory errors in variant save */ }
        alert(`Variant ${data.id} created`);
      } else {
        const bodyUpdate = {
          sku: v.sku,
          size: v.size,
          color: v.color,
          material: v.material,
          price_cents: v.price_cents,
          currency: v.currency,
          stock: v.stock,
        };
        await apiClient.put(`/product-variants/${v.id}`, bodyUpdate);
        // Ensure inventory reflects desired stock
        try {
          await apiClient.post('/inventory', { variant_id: v.id, stock: Number.isFinite(v.stock) ? Number(v.stock) : 0 });
        } catch (_) { /* ignore inventory errors in variant update */ }
        alert(`Variant ${v.id} updated`);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save variant');
    } finally {
      setSaving(false);
    }
  };

  const saveAllVariants = async () => {
    if (!product?.id || !Array.isArray(product.variants) || product.variants.length === 0) return;
    setSaving(true);
    setError('');
    const errors = [];
    
    try {
      // Process variants to filter out ones without sizes and generate SKUs
      const { processedVariants } = processVariantsForSaving(product.variants, product.sku);
      
      if (processedVariants.length === 0) {
        alert('No variants with sizes found to save. Please ensure all variants have sizes assigned.');
        return;
      }
      
      console.log(`Saving ${processedVariants.length} variants`);
      
      // Update local state with processed variants (removes variants without sizes)
      setProduct(p => ({ ...p, variants: processedVariants }));
      
      // Process each variant
      for (let idx = 0; idx < processedVariants.length; idx++) {
        const v = processedVariants[idx];
        
        try {
          if (v.isDraft || !v.id) {
            // Create new variant in backend
            console.log(`Creating draft variant: ${v.color} ${v.size} with ${v.images?.length || 0} images`);
            
            const bodyCreate = {
              product_id: product.id,
              sku: v.sku,
              size: v.size,
              color: v.color || undefined,
              material: v.material || undefined,
              price_cents: v.price_cents ?? product.price_cents ?? 0,
              currency: v.currency || product.currency || 'EUR',
            };
            
            const { data } = await apiClient.post('/product-variants', bodyCreate);
            const newVariantId = data.id;
            
            // Update local state with new ID and mark as no longer draft
            setProduct(p => {
              const vs = [...p.variants];
              vs[idx] = { 
                ...vs[idx], 
                id: newVariantId, 
                sku: data.sku || bodyCreate.sku,
                isDraft: false 
              };
              return { ...p, variants: vs };
            });
            
            // Upload images for this variant if it has any
            if (v.images && v.images.length > 0) {
              console.log(`Uploading ${v.images.length} images for variant ${newVariantId}`);
              const uploadedImages = [];
              
              for (const image of v.images) {
                if (image.isDraft && image.image_url && image.image_url.startsWith('data:')) {
                  try {
                    console.log(`Uploading image for variant ${newVariantId}: ${image.view_type}`);
                    const response = await apiClient.post(`/products/${product.id}/variants/${newVariantId}/images`, {
                      image: image.image_url,
                      view_type: image.view_type || 'front',
                      is_primary: image.is_primary || false,
                      position: image.position || 0
                    });
                    
                    // Store the uploaded image response
                    uploadedImages.push({
                      id: response.data.id,
                      image_url: response.data.image_url,
                      alt_text: response.data.alt_text,
                      view_type: response.data.view_type,
                      is_primary: response.data.is_primary,
                      position: response.data.position
                    });
                    
                    console.log(`Successfully uploaded image ${response.data.id} for variant ${newVariantId}`);
                  } catch (imgError) {
                    console.error(`Failed to upload image for variant ${newVariantId}:`, imgError);
                    errors.push(`Failed to upload image for variant ${v.sku}: ${imgError.response?.data?.message || imgError.message}`);
                  }
                }
              }
              
              // Update variant with the uploaded images immediately
              if (uploadedImages.length > 0) {
                setProduct(p => {
                  const vs = [...p.variants];
                  const vIndex = vs.findIndex(variant => variant.id === newVariantId);
                  if (vIndex !== -1) {
                    vs[vIndex].images = uploadedImages;
                    console.log(`Updated variant ${newVariantId} with ${uploadedImages.length} uploaded images`);
                  }
                  return { ...p, variants: vs };
                });
              }
            }
            
            // Set inventory
            try {
              await apiClient.post('/inventory', { 
                variant_id: newVariantId, 
                stock: Number.isFinite(v.stock) ? Number(v.stock) : 0 
              });
            } catch (_) {}
            
          } else {
            // Update existing variant
            console.log(`Updating existing variant: ${v.id} - ${v.color} ${v.size}`);
            
            const bodyUpdate = {
              sku: v.sku,
              size: v.size,
              color: v.color,
              material: v.material,
              price_cents: v.price_cents,
              currency: v.currency,
            };
            
            await apiClient.put(`/product-variants/${v.id}`, bodyUpdate);
            
            // Update inventory
            try {
              await apiClient.post('/inventory', { 
                variant_id: v.id, 
                stock: Number.isFinite(v.stock) ? Number(v.stock) : 0 
              });
            } catch (_) {}
          }
          
        } catch (e) {
          console.error(`Failed to save variant ${v.sku || v.size}:`, e);
          errors.push(`Variant ${v.sku || v.size}: ${e.response?.data?.message || e.message || 'Failed'}`);
        }
      }
      
      // Set up collapsed state for similar variants
      const groups = groupVariantsByAttributes(processedVariants);
      const newCollapsedState = {};
      Object.keys(groups).forEach(groupKey => {
        const group = groups[groupKey];
        if (shouldCollapseGroup(group)) {
          group.slice(1).forEach(item => {
            newCollapsedState[item.index] = true;
          });
        }
      });
      setCollapsedVariants(newCollapsedState);
      
      if (errors.length > 0) {
        setError(errors.join('\n'));
        alert(`${processedVariants.length - errors.length} variants saved successfully. ${errors.length} failed - check error details.`);
      } else {
        alert(`All ${processedVariants.length} variants saved successfully!`);
      }
      
    } catch (e) {
      console.error('Error in saveAllVariants:', e);
      setError(`Failed to save variants: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addVariant = () => {
    if (!product?.id) return;
    // Create a unique temporary ID for draft variants
    const draftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setProduct(p => ({
      ...p,
      variants: [...(p?.variants || []), {
        id: undefined, // No ID means it's a draft
        draftId: draftId, // Temporary ID for tracking
        isDraft: true, // Flag to indicate draft status
        sku: '', // Will be generated when saved
        size: '',
        color: '',
        material: '',
        price_cents: p.price_cents || 0,
        currency: p.currency || 'EUR',
        stock: 0,
        images: [], // Will store base64 images for drafts
      }]
    }));
  };

  const uploadVariantImage = async (idx, file) => {
    let v = product?.variants?.[idx];
    if (!v || !file) return;
    
    // Client-side size guard: 50KB max
    if (file.size > 50 * 1024) {
      setVariantUploadErrors(prev => ({ ...prev, [idx]: 'Image too large. Max size is 50KB' }));
      return;
    }
    
    setSaving(true);
    setVariantUploadErrors(prev => ({ ...prev, [idx]: '' }));
    
    try {
      // Convert to base64
      const toBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      
      const base64 = await toBase64(file);
      
      // Handle draft variants - store image in frontend state only
      if (v.isDraft) {
        // For draft variants, store base64 image in frontend state
        const imageObj = {
          id: `draft_img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          image_url: base64, // Store base64 data URL
          alt_text: '',
          view_type: 'front',
          is_primary: v.images.length === 0, // First image is primary
          position: v.images.length,
          isDraft: true // Flag to indicate this is a draft image
        };
        
        // If variant has comma-separated sizes, split first and add image to all
        if (v.size && v.size.includes(',')) {
          const split = splitVariantBySizes(v, product.sku, product.variants);
          if (split && split.length > 1) {
            // Add image to all split variants and normalize primary flag per variant
            split.forEach(splitVariant => {
              const newImg = { ...imageObj, id: `${imageObj.id}_${splitVariant.size}` };
              const imgs = [...(splitVariant.images || []), newImg];
              let foundPrimary = false;
              splitVariant.images = imgs.map(img => {
                if (img.is_primary) {
                  if (!foundPrimary) {
                    foundPrimary = true;
                    return img;
                  }
                  return { ...img, is_primary: false };
                }
                return img;
              });
            });
            
            const newVariants = [
              ...product.variants.slice(0, idx),
              ...split,
              ...product.variants.slice(idx + 1)
            ];
            setProduct(p => ({ ...p, variants: newVariants }));
            setVariantUploadErrors(prev => ({ ...prev, [idx]: `Image added to ${split.length} draft variants` }));
          }
        } else {
          // Add image to single draft variant
          setProduct(p => {
            const vs = [...p.variants];
            const existing = Array.isArray(vs[idx].images) ? [...vs[idx].images] : [];
            const imgs = [...existing, imageObj];
            // Normalize: ensure only one primary image for this variant
            let foundPrimary = false;
            const normalized = imgs.map(img => {
              if (img.is_primary) {
                if (!foundPrimary) {
                  foundPrimary = true;
                  return img;
                }
                return { ...img, is_primary: false };
              }
              return img;
            });
            vs[idx] = { ...vs[idx], images: normalized };
            return { ...p, variants: vs };
          });
          setVariantUploadErrors(prev => ({ ...prev, [idx]: 'Image added to draft variant' }));
        }
      } else {
        // Handle existing variants - upload to server immediately
        if (v.size && v.size.includes(',')) {
          // Split the variant first
          const split = splitVariantBySizes(v, product.sku, product.variants);
          if (split && split.length > 1) {
            // Update variants in state
            const newVariants = [
              ...product.variants.slice(0, idx),
              ...split,
              ...product.variants.slice(idx + 1)
            ];
            setProduct(p => ({ ...p, variants: newVariants }));
            
            // Upload image to all split variants
            for (let i = 0; i < split.length; i++) {
              const splitVariantIdx = idx + i;
              await uploadImageToServer(splitVariantIdx, { base64 });
            }
            
            setVariantUploadErrors(prev => ({ ...prev, [idx]: `Image uploaded to ${split.length} variants` }));
          }
        } else {
          // Regular existing variant - upload directly
          await uploadImageToServer(idx, { base64 });
          setVariantUploadErrors(prev => ({ ...prev, [idx]: 'Image uploaded successfully' }));
        }
      }
      
    } catch (error) {
      setVariantUploadErrors(prev => ({ ...prev, [idx]: error.message || 'Failed to upload image' }));
    } finally {
      setSaving(false);
    }
  };

  // Function to copy images from one variant to another
  const copyVariantImages = async (fromVariantId, toVariantId) => {
    try {
      // Get images from source variant
      const fromVariant = product.variants.find(v => v.id === fromVariantId);
      if (!fromVariant || !fromVariant.images || fromVariant.images.length === 0) {
        return;
      }
      
      // Copy each image to the new variant
      for (const image of fromVariant.images) {
        // Convert image URL to base64 for re-upload
        const imageResponse = await fetch(image.image_url);
        const imageBlob = await imageResponse.blob();
        
        // Convert blob to base64
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(imageBlob);
        });
        
        await apiClient.post(`/products/${product.id}/variants/${toVariantId}/images`, {
          image: base64,
          view_type: image.view_type || 'front',
          is_primary: image.is_primary || false,
          position: image.position || 0
        });
      }
    } catch (error) {
      console.error('Failed to copy images:', error);
      throw error;
    }
  };

  // New function to actually upload images to server
  const uploadImageToServer = async (idx, imageData) => {
    let v = product?.variants?.[idx];
    if (!v || !imageData) return null;
    
    try {
      // Create variant first if not persisted yet
      if (!v.id) {
        const bodyCreate = {
          product_id: product.id,
          sku: v.sku || generateVariantSku(product.sku, v.size, v.color),
          size: v.size || undefined,
          color: v.color || undefined,
          material: v.material || undefined,
          price_cents: v.price_cents ?? product.price_cents ?? 0,
          currency: v.currency || product.currency || 'EUR',
        };
        const { data } = await apiClient.post('/product-variants', bodyCreate);
        setProduct(p => {
          const vs = [...p.variants];
          vs[idx] = { ...vs[idx], id: data.id, sku: data.sku || bodyCreate.sku };
          return { ...p, variants: vs };
        });
        // Upsert inventory stock for the newly created variant
        try {
          await apiClient.post('/inventory', { variant_id: data.id, stock: Number.isFinite(v.stock) ? Number(v.stock) : 0 });
        } catch (_) { /* ignore inventory errors during image upload path */ }
        v = { ...v, id: data.id };
      }

      const { data } = await apiClient.post(`/products/${product.id}/variants/${v.id}/images`, {
        image: imageData.base64,
        view_type: 'front',
        is_primary: false,
        position: (v.images?.length || 0)
      });
      
      // Append new image to variant
      setProduct(p => {
        const vs = [...p.variants];
        // Clone existing images
        let imgs = Array.isArray(vs[idx].images) ? [...vs[idx].images] : [];
        // Normalize existing images to ensure only one is marked primary at most
        let foundPrimary = false;
        imgs = imgs.map(img => {
          if (img.is_primary) {
            if (!foundPrimary) {
              foundPrimary = true;
              return img;
            }
            return { ...img, is_primary: false };
          }
          return img;
        });
        // Determine if we already have a primary after normalization
        const hasPrimary = foundPrimary || imgs.some(i => i.is_primary);
        // Push the newly uploaded image, forcing is_primary so only one exists
        imgs.push({
          id: data.id,
          image_url: data.image_url,
          alt_text: data.alt_text,
          view_type: data.view_type,
          is_primary: hasPrimary ? false : true,
          position: data.position
        });
        vs[idx] = { ...vs[idx], images: imgs };
        return { ...p, variants: vs };
      });
      
      return data;
    } catch (e) {
      throw new Error(e.response?.data?.message || 'Failed to upload image');
    }
  };

  const deleteVariantImage = async (variantIdx, imageId) => {
    if (!imageId) return;
    setSaving(true);
    setError('');
    try {
      await apiClient.delete(`/products/product-images/${imageId}`);
      setProduct(p => {
        const vs = [...p.variants];
        const imgs = (vs[variantIdx].images || []).filter(img => img.id !== imageId);
        vs[variantIdx] = { ...vs[variantIdx], images: imgs };
        return { ...p, variants: vs };
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete image');
    } finally {
      setSaving(false);
    }
  };

  const setPrimaryVariantImage = async (variantIdx, imageId) => {
    if (!imageId) return;
    setSaving(true);
    setError('');
    try {
      await apiClient.put(`/products/product-images/${imageId}`, { is_primary: true });
      // Update local state: mark this image primary and unset others for that variant
      setProduct(p => {
        const vs = [...p.variants];
        const imgs = (vs[variantIdx].images || []).map(img => ({ ...img, is_primary: img.id === imageId }));
        vs[variantIdx] = { ...vs[variantIdx], images: imgs };
        return { ...p, variants: vs };
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to set primary image');
    } finally {
      setSaving(false);
    }
  };

  const deleteVariant = async (idx) => {
    const v = product?.variants?.[idx];
    if (!v?.id) return;
    if (!window.confirm(`Delete variant #${v.id}? This cannot be undone.`)) return;
    setSaving(true);
    setError('');
    try {
      await apiClient.delete(`/product-variants/${v.id}`);
      setProduct(p => {
        const vs = [...(p.variants || [])];
        vs.splice(idx, 1);
        return { ...p, variants: vs };
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete variant');
    } finally {
      setSaving(false);
    }
  };

  const uploadProductCover = async (file) => {
    if (!product?.id || !file) return;
    // Client-side size guard: 50KB max
    if (file.size > 50 * 1024) {
      setCoverUploadError('Image too large. Max size is 50KB');
      return;
    }
    setSaving(true);
    setCoverUploadError('');
    try {
      const form = new FormData();
      form.append('image', file);
      const { data } = await apiClient.post(`/products/${product.id}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProduct(p => ({ ...p, image_url: data.image_url }));
    } catch (e) {
      setCoverUploadError(e.response?.data?.message || 'Failed to upload product image');
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async () => {
    if (!product?.id) return;
    if (!window.confirm(`Delete product #${product.id}? This cannot be undone.`)) return;
    setSaving(true);
    setError('');
    try {
      await apiClient.delete(`/products/${product.id}`);
      alert('Product deleted');
      // Refresh list and clear current selection
      setProduct(null);
      setSelectedId(null);
      await loadList({ offset: 0 });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Update Products</h1>
          <div className="flex items-center gap-3">
            {product && (
              <div className="text-xs text-white/60 hidden sm:block">Editing ID {product.id} • SKU {product.sku || '—'}</div>
            )}
            {product && (
              <button
                onClick={deleteProduct}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-600 text-white text-sm shadow-sm disabled:opacity-60"
                title="Delete this product"
              >
                {saving ? 'Deleting…' : 'Delete Product'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <aside className="lg:col-span-1 rounded-2xl border border-white/10 bg-white/5 p-4 sticky top-16 h-fit shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white/90">Filters</h3>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded btn-ghost hover:bg-white/10"
                onClick={() => { setParentCategoryId(''); setSubcategoryId(''); setListQuery(''); setListOffset(0); loadList({ offset: 0, search: '' }); }}
                title="Clear all filters"
              >
                Clear
              </button>
            </div>
            <div className="flex gap-2">
              <input
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder="Search by name or SKU…"
                className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder-white/40"
              />
              <button onClick={() => { setListOffset(0); loadList({ offset: 0, search: listQuery }); }} disabled={listLoading} className="px-3 py-2 rounded-xl btn-primary text-sm shadow-sm disabled:opacity-60">
                {listLoading ? '...' : 'Search'}
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs mb-1 text-white/70">Parent category</label>
                <select
                  value={parentCategoryId}
                  onChange={(e) => { setParentCategoryId(e.target.value); setSubcategoryId(''); }}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">All parents</option>
                  {parentCategories.map(pc => (
                    <option key={pc.id} value={pc.id}>{pc.display_name || pc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1 text-white/70">Subcategory</label>
                <select
                  value={subcategoryId}
                  onChange={(e) => setSubcategoryId(e.target.value)}
                  disabled={!parentCategoryId}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                >
                  <option value="">{parentCategoryId ? 'All subcategories' : 'Select parent first'}</option>
                  {subcategories.map(sc => (
                    <option key={sc.id} value={sc.id}>{sc.display_name || sc.name}</option>
                  ))}
                </select>
              </div>
              {(parentCategoryId || subcategoryId || listQuery) && (
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/70">
                  {listQuery && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/10">search: "{listQuery}"</span>
                  )}
                  {parentCategoryId && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/10">parent: {(() => { const f = parentCategories.find(p => String(p.id) === String(parentCategoryId)); return (f?.display_name || f?.name || parentCategoryId); })()}</span>
                  )}
                  {subcategoryId && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/10">sub: {(() => { const f = subcategories.find(s => String(s.id) === String(subcategoryId)); return (f?.display_name || f?.name || subcategoryId); })()}</span>
                  )}
                </div>
              )}
            </div>
            {listError && <div className="mt-3 text-sm text-red-400">{listError}</div>}
            <div className="mt-3 max-h-[60vh] overflow-auto">
              {list.map(item => {
                const isActive = Number(selectedId) === Number(item.id);
                return (
                  <button key={item.id} onClick={() => loadById(item.id, item.description)} className={`w-full text-left px-2 py-2 rounded-xl mb-1 transition ${isActive ? 'bg-cyan-600/20 border border-cyan-400/30 shadow-sm' : 'hover:bg-white/10 border border-transparent'}`}>
                    <div className="flex items-center gap-3">
                      {item.image_url ? (
                        <img src={item.image_url} alt="" className="w-10 h-10 rounded object-cover border border-white/10 ring-1 ring-white/10" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-white/10" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{item.display_name || item.name || 'Untitled'}</div>
                        {item.description && (
                          <div className="text-[11px] text-white/60 truncate">{item.description}</div>
                        )}
                        <div className="text-[11px] text-white/60 truncate flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 bg-white/10 px-1.5 py-0.5 rounded">ID {item.id}</span>
                          <span className="inline-flex items-center gap-1 bg-white/10 px-1.5 py-0.5 rounded">SKU {item.sku || '—'}</span>
                        </div>
                      </div>
                      <div className="text-[11px] text-white/70 whitespace-nowrap">{item.currency} {(item.price_cents/100).toFixed(2)}</div>
                    </div>
                  </button>
                );
              })}
              {(!listLoading && list.length === 0) && (
                <div className="text-sm text-white/60 py-4">No products found</div>
              )}
              {listLoading && (
                <div className="text-sm text-white/60 py-2">Loading…</div>
              )}
            </div>
          </aside>

          <section className="lg:col-span-2">
            {error && <div className="mb-3 text-sm text-red-400">{error}</div>}
            {product && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-semibold tracking-tight">Core</h2>
                    <div className="text-xs text-white/60">Changes are not auto-saved</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1 text-white/80">SKU</label>
                      <input value={product.sku} onChange={(e) => setProduct(p => ({ ...p, sku: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="e.g. team-2025-home-xs" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-white/80">Name</label>
                      <input value={product.name} onChange={(e) => setProduct(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Product name" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-white/80">Display name</label>
                      <input value={product.display_name} onChange={(e) => setProduct(p => ({ ...p, display_name: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Shown to users" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-white/80">Product URL</label>
                      <input value={product.product_url} onChange={(e) => setProduct(p => ({ ...p, product_url: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="https://…" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-white/80">Price (cents)</label>
                      <input type="number" value={product.price_cents} onChange={(e) => setProduct(p => ({ ...p, price_cents: parseInt(e.target.value || '0', 10) }))} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-white/80">Currency</label>
                      <input value={product.currency} onChange={(e) => setProduct(p => ({ ...p, currency: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="EUR" />
                    </div>
                    <div className="md:col-span-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm mb-1 text-white/80">Parent category</label>
                          <select
                            value={editParentCategoryId}
                            onChange={(e) => { setEditParentCategoryId(e.target.value); setEditSubcategoryId(''); }}
                            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="">None</option>
                            {allParentCategories.map(pc => (
                              <option key={pc.id} value={pc.id}>{pc.display_name || pc.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm mb-1 text-white/80">Subcategory</label>
                          <select
                            value={editSubcategoryId}
                            onChange={(e) => setEditSubcategoryId(e.target.value)}
                            disabled={!editParentCategoryId}
                            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                          >
                            <option value="">{editParentCategoryId ? 'None' : 'Select parent first'}</option>
                            {allSubcategories
                              .filter(sc => String(sc.parent_id) === String(editParentCategoryId))
                              .map(sc => (
                                <option key={sc.id} value={sc.id}>{sc.display_name || sc.name}</option>
                              ))}
                          </select>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button onClick={saveCategories} disabled={saving} className="px-4 py-2 rounded-xl btn-ghost hover:bg-white/10">{saving ? 'Saving…' : 'Save Categories'}</button>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm mb-1 text-white/80">Description</label>
                      <textarea value={product.description} onChange={(e) => setProduct(p => ({ ...p, description: e.target.value }))} rows={4} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Short description…" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm mb-1 text-white/80">Cover image</label>
                      <div className="flex items-center gap-3">
                        <input value={product.image_url} onChange={(e) => setProduct(p => ({ ...p, image_url: e.target.value }))} className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="https://…" />
                        <label className="px-3 py-1.5 rounded-xl btn-ghost text-sm cursor-pointer whitespace-nowrap hover:bg-white/10">
                          Upload Cover
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadProductCover(f); e.target.value=''; }} />
                        </label>
                        {product.image_url && (
                          <img src={product.image_url} alt="" className="w-14 h-14 rounded object-cover border border-white/10 ring-1 ring-white/10" />
                        )}
                      </div>
                      {coverUploadError && (
                        <div className="mt-2 text-xs text-red-400">{coverUploadError}</div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <div className="flex items-center gap-2">
                      <button onClick={() => loadById(product.id)} disabled={saving} className="px-4 py-2 rounded-xl btn-ghost hover:bg-white/10">Discard</button>
                      <button onClick={saveCore} disabled={saving} className="px-5 py-2 rounded-xl btn-primary shadow-sm">{saving ? 'Saving…' : 'Save Core'}</button>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold tracking-tight">Variants</h2>
                    {product && (
                      <div className="flex items-center gap-2">
                        <button onClick={addVariant} disabled={saving} className="px-3 py-1.5 rounded-lg btn-ghost text-sm shadow-sm hover:bg-white/10">Add Variant</button>
                        <button onClick={saveAllVariants} disabled={saving} className="px-3 py-1.5 rounded-lg btn-primary text-sm shadow-sm">{saving ? 'Saving…' : 'Save All Variants'}</button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    {product.variants.map((v, idx) => {
                      const isCollapsed = collapsedVariants[idx];
                      const groups = groupVariantsByAttributes(product.variants);
                      const currentGroup = Object.values(groups).find(group => 
                        group.some(item => item.index === idx)
                      );
                      const isGrouped = currentGroup && currentGroup.length > 1;
                      
                      return (
                        <div key={v.id || idx} className="border border-white/10 rounded-xl p-3 bg-white/5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-sm">
                              {isGrouped && (
                                <button
                                  onClick={() => setCollapsedVariants(prev => ({
                                    ...prev,
                                    [idx]: !prev[idx]
                                  }))}
                                  className="text-white/60 hover:text-white/80"
                                  title={isCollapsed ? "Expand variant" : "Collapse variant"}
                                >
                                  {isCollapsed ? "▶" : "▼"}
                                </button>
                              )}
                              {v.isDraft ? (
                                <span className="px-2 py-0.5 rounded bg-yellow-600/20 text-yellow-200">DRAFT</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded bg-white/10">#{v.id}</span>
                              )}
                              {v.color && <span className="px-2 py-0.5 rounded bg-purple-600/20 text-purple-200">{v.color}</span>}
                              {v.size ? (
                                <span className="px-2 py-0.5 rounded bg-cyan-600/20 text-cyan-200">{v.size}</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded bg-red-600/20 text-red-200">No Size</span>
                              )}
                              {v.sku && <span className="px-2 py-0.5 rounded bg-white/10">{v.sku}</span>}
                              {v.images && v.images.length > 0 && <span className="px-2 py-0.5 rounded bg-green-600/20 text-green-200">📷 {v.images.length}</span>}
                            </div>
                            <button
                              className="text-xs px-2 py-1 rounded bg-red-600/80 hover:bg-red-600 text-white shadow-sm"
                              onClick={() => deleteVariant(idx)}
                              title="Delete this variant"
                            >
                              {saving ? 'Deleting…' : 'Delete Variant'}
                            </button>
                          </div>
                          {!isCollapsed && (
                            <>
                        <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3`}>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">SKU</label>
                            <input value={v.sku || generateVariantSku(product.sku, v.size, v.color)} onChange={(e) => setProduct(p => { const vs = [...p.variants]; vs[idx] = { ...vs[idx], sku: e.target.value }; return { ...p, variants: vs }; })} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                          </div>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">Size</label>
                            <input
                              value={v.size}
                              onChange={(e) => setProduct(p => {
                                const vs = [...p.variants];
                                const next = { ...vs[idx], size: e.target.value };
                                next.sku = next.sku || generateVariantSku(p.sku, next.size, next.color);
                                vs[idx] = next;
                                return { ...p, variants: vs };
                              })}
                              onBlur={() => {
                                setProduct(p => {
                                  const current = p.variants[idx];
                                  if (!current?.size || !String(current.size).includes(',')) return p;
                                  const split = splitVariantBySizes(current, p.sku, p.variants);
                                  if (!split || split.length <= 1) return p;
                                  
                                  console.log(`Splitting variant with sizes: ${current.size} into ${split.length} variants`);
                                  
                                  // For draft variants, ensure split variants are also drafts and copy all properties
                                  if (current.isDraft) {
                                    split.forEach((splitVariant, i) => {
                                      splitVariant.isDraft = true;
                                      splitVariant.draftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${i}`;
                                      splitVariant.id = undefined; // Ensure no ID for drafts
                                      
                                      // Copy images from original variant
                                      if (current.images && current.images.length > 0) {
                                        splitVariant.images = current.images.map(img => ({
                                          ...img,
                                          id: `${img.id}_${splitVariant.size}` // Unique ID for each split
                                        }));
                                      }
                                    });
                                  } else {
                                    // For existing variants, copy images normally
                                    if (current.images && current.images.length > 0) {
                                      split.forEach(splitVariant => {
                                        splitVariant.images = [...current.images];
                                      });
                                    }
                                  }
                                  
                                  console.log(`Copied ${current.images?.length || 0} images to ${split.length} split variants`);
                                  
                                  const newVariants = [
                                    ...p.variants.slice(0, idx),
                                    ...split,
                                    ...p.variants.slice(idx + 1)
                                  ];
                                  
                                  console.log(`Replaced 1 variant with ${split.length} variants. Total variants: ${newVariants.length}`);
                                  
                                  // Set up collapsed state for the new variants (collapse all but the first)
                                  if (split.length > 1) {
                                    setCollapsedVariants(prev => {
                                      const newState = { ...prev };
                                      for (let i = 1; i < split.length; i++) {
                                        newState[idx + i] = true;
                                      }
                                      return newState;
                                    });
                                  }
                                  
                                  return { ...p, variants: newVariants };
                                });
                              }}
                              placeholder="S, M, L"
                              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">Color</label>
                            <input value={v.color} onChange={(e) => setProduct(p => { const vs = [...p.variants]; const next = { ...vs[idx], color: e.target.value }; next.sku = next.sku || generateVariantSku(p.sku, next.size, next.color); vs[idx] = next; return { ...p, variants: vs }; })} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                          </div>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">Material</label>
                            <input value={v.material} onChange={(e) => setProduct(p => { const vs = [...p.variants]; vs[idx] = { ...vs[idx], material: e.target.value }; return { ...p, variants: vs }; })} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                          </div>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">Price (cents)</label>
                            <input type="number" value={v.price_cents} onChange={(e) => setProduct(p => { const vs = [...p.variants]; vs[idx] = { ...vs[idx], price_cents: parseInt(e.target.value || '0', 10) }; return { ...p, variants: vs }; })} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                          </div>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">Currency</label>
                            <input value={v.currency} onChange={(e) => setProduct(p => { const vs = [...p.variants]; vs[idx] = { ...vs[idx], currency: e.target.value }; return { ...p, variants: vs }; })} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                          </div>
                          <div>
                            <label className="block text-sm mb-1 text-white/80">Stock</label>
                            <input type="number" value={v.stock} onChange={(e) => setProduct(p => { const vs = [...p.variants]; vs[idx] = { ...vs[idx], stock: parseInt(e.target.value || '0', 10) }; return { ...p, variants: vs }; })} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <button onClick={() => saveVariant(idx)} disabled={saving} className="px-3 py-1.5 rounded-xl btn-primary text-sm shadow-sm">{saving ? 'Saving…' : 'Save Variant'}</button>
                          <label className="px-3 py-1.5 rounded-xl btn-ghost text-sm cursor-pointer hover:bg-white/10">
                            Upload Images
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={(e) => {
                                const selected = Array.from(e.target.files || []);
                                e.target.value = '';
                                if (!selected.length) return;
                                (async () => {
                                  for (const file of selected) {
                                    await uploadVariantImage(idx, file);
                                  }
                                })();
                              }}
                            />
                          </label>
                        </div>
                        {variantUploadErrors[idx] && (
                          <div className="mt-2 text-xs text-red-400">{variantUploadErrors[idx]}</div>
                        )}
                        
                        {/* Variant Images */}
                        {(v.images?.length || 0) > 0 && (
                          <div className="mt-3">
                            <div className="text-sm text-white/80 mb-2">Images ({v.images.length}):</div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                              {v.images.map(img => (
                                <div key={img.id} className={`relative group ${img.is_primary ? 'ring-2 ring-cyan-500 rounded' : ''}`}>
                                  {img.is_primary && <span className="absolute top-1 left-1 text-[10px] bg-cyan-600 text-white px-1.5 py-0.5 rounded">Primary</span>}
                                  <img src={img.image_url} alt={img.alt_text || ''} className="w-full h-28 object-cover rounded border border-white/10 ring-1 ring-white/10" />
                                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {!img.is_primary && (
                                      <button onClick={() => setPrimaryVariantImage(idx, img.id)} className="text-xs bg-cyan-600/80 hover:bg-cyan-600 text-white px-2 py-0.5 rounded shadow-sm">Make primary</button>
                                    )}
                                    <button onClick={() => deleteVariantImage(idx, img.id)} className="text-xs bg-red-600/80 hover:bg-red-600 text-white px-2 py-0.5 rounded shadow-sm">Delete</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {!product && (
              <div className="text-sm text-white/60">Select a product from the list to edit.</div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
